import type { Clinic } from "./clinics";

export const PHONE = "+1-866-488-9356";

/**
 * Safely serialize data for embedding inside a <script type="application/ld+json">
 * tag. JSON.stringify does not escape `<`, `>`, `&`, or `/`, so a string like
 * `</script><script>...</script>` could break out of the script context and
 * execute attacker-controlled JavaScript. Replace those characters with their
 * unicode escapes so the JSON remains valid but cannot terminate the tag.
 */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function postalAddress(street: string, city: string, region: string) {
  return {
    "@type": "PostalAddress",
    streetAddress: street,
    addressLocality: city,
    addressRegion: region,
    addressCountry: "US",
  };
}

export function locationSchema(
  name: string,
  street: string,
  city: string,
  region: string,
  phone: string,
  pageUrl: string,
  description: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "LocalBusiness"],
    name: `RESTORE Hair — ${name}`,
    description,
    url: pageUrl,
    telephone: phone,
    address: postalAddress(street, city, region),
  };
}

export const SITE_URL = "https://restorehair.com";
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

/** Asset pointers are site-relative; schema.org needs absolute URLs. */
export function absUrl(url: string) {
  return url.startsWith("http") ? url : `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

/** Sitewide publisher identity, emitted once from the root route. */
export function organizationSchema(logoUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalOrganization", "Organization"],
    "@id": ORGANIZATION_ID,
    name: "RESTORE Hair",
    alternateName: "RESTORE Hair Transplants",
    url: SITE_URL,
    logo: absUrl(logoUrl),
    image: absUrl(logoUrl),
    telephone: PHONE,
    medicalSpecialty: "Dermatology",
    sameAs: ["https://www.instagram.com/restore_hair/", "https://www.youtube.com/@restore_hair"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
  };
}

export function physicianSchema(opts: {
  name: string;
  jobTitle: string;
  description: string;
  image: string;
  pageUrl: string;
  /** The clinic where the doctor actually practices. Defaults to Denver HQ. */
  clinic?: { street: string; city: string; state: string };
}) {
  const clinic = opts.clinic ?? {
    street: "5445 DTC Pkwy, Suite 1015",
    city: "Greenwood Village",
    state: "CO",
  };
  // The root route emits the Organization node on every page with this @id.
  const org = { "@id": ORGANIZATION_ID };
  return {
    "@context": "https://schema.org",
    // A doctor is a person. Physician (an Organization and LocalBusiness
    // type in Schema.org) keeps the medical listing fields. With both types,
    // the person fields (jobTitle, worksFor, affiliation) are valid.
    "@type": ["Person", "Physician"],
    name: opts.name,
    jobTitle: opts.jobTitle,
    description: opts.description,
    image: absUrl(opts.image),
    url: opts.pageUrl,
    medicalSpecialty: "Dermatology",
    telephone: PHONE,
    priceRange: "$$$",
    address: postalAddress(clinic.street, clinic.city, clinic.state),
    worksFor: org,
    affiliation: org,
    memberOf: org,
  };
}

/** Physician node and the Home > Doctors > doctor breadcrumb, for a doctor page. */
export function physicianPageSchema(opts: Parameters<typeof physicianSchema>[0]) {
  return {
    "@graph": [
      physicianSchema(opts),
      breadcrumbSchema([
        { name: "Doctors", path: "/doctors/" },
        { name: opts.name, path: new URL(opts.pageUrl).pathname },
      ]),
    ],
  };
}

/** Blog archive pages (category / tag / author). */
export function collectionPageSchema(name: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * Clinic list for a state hub page. Each item is a link (name and page URL),
 * not a business node: the clinic node belongs on the clinic's own page.
 */
export function stateHubSchema(
  stateName: string,
  path: string,
  cities: { name: string; to?: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `RESTORE Hair locations in ${stateName}`,
    url: `${SITE_URL}${path}`,
    itemListElement: cities.map((city, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: `RESTORE Hair ${city.name}`,
      ...(city.to && city.to !== path ? { url: absUrl(city.to) } : {}),
    })),
  };
}


/** One physical clinic, emitted only on the clinic's own page. */
export function clinicSchema(clinic: Clinic) {
  const url = absUrl(clinic.page ?? "/locations/");
  return {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "@id": `${url}#clinic`,
    name: `RESTORE Hair ${clinic.name}`,
    url,
    telephone: PHONE,
    address: {
      "@type": "PostalAddress",
      streetAddress: clinic.street,
      addressLocality: clinic.city,
      addressRegion: clinic.region,
      postalCode: clinic.postalCode,
      addressCountry: "US",
    },
    ...(clinic.geo
      ? { geo: { "@type": "GeoCoordinates", latitude: clinic.geo.lat, longitude: clinic.geo.lng } }
      : {}),
    parentOrganization: { "@id": ORGANIZATION_ID },
  };
}

/**
 * Clinic list for a regional hub page. Each item links to the clinic's own
 * page. A clinic with no page of its own is listed by name only.
 */
export function clinicListSchema(name: string, path: string, clinics: Clinic[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: absUrl(path),
    itemListElement: clinics.map((clinic, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: `RESTORE Hair ${clinic.name}`,
      ...(clinic.page ? { url: absUrl(clinic.page) } : {}),
    })),
  };
}

/** BreadcrumbList from Home to the current page. Paths are site-relative. */
export function breadcrumbSchema(items: { name: string; path: string }[]) {
  const all = [{ name: "Home", path: "/" }, ...items];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: all.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absUrl(item.path),
    })),
  };
}

/**
 * Breadcrumb for a city page, /locations/<state>/<city>/:
 * Home > Locations > <State> > <City>. The state name comes from the slug.
 */
export function locationPageCrumbs(pageUrl: string, cityName: string) {
  const path = new URL(pageUrl).pathname;
  const state = path.split("/")[2] ?? "";
  const stateName = state
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return breadcrumbSchema([
    { name: "Locations", path: "/locations/" },
    { name: stateName, path: `/locations/${state}/` },
    { name: cityName, path },
  ]);
}
